import { createGlobalStyle } from 'styled-components/macro';

/*
 * Global style
 *
 * Normally, styled components are automatically scoped to a local CSS class and
 * therefore isolated from other components. In the case of createGlobalStyle,
 * this limitation is removed and things like CSS resets or base stylesheets can be applied.
 *
 * Source and more info: https://styled-components.com/docs/api#createglobalstyle
 */
export default createGlobalStyle`
  body {
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .text-muted {
    font-weight: 300;
  }

  .text-error {
    color: #ef476f !important;
  }

  .text-italic {
    font-style: italic;
  }

  .u-underline {
    text-decoration: underline;
  }

  .u-visibility--hidden {
    visibility: hidden;
  }

  .u-color-inherit {
    color: inherit;

    &:hover {
      color: inherit;
      opacity: .9;
    }
  }

  .u-inline {
    display: inline !important;
  }

  // Custom select
  .c-custom-select {
    .cs__menu {
      // Otherwise radio buttons display over select menu
      z-index: 10;
    }

    .cs__option {
      display: flex;
      flex-direction: inherit;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;

      &--is-selected {
        background: #eaf8f5;
        color: inherit;
      }

      &--is-disabled {
        opacity: .8;
      }
    }

    .cs__group {
      padding: 4px 0 0;

      .cs__option {
        padding-left: 1.5rem;
      }
    }
  }

  .c-filter {
    min-width: 160px;
    max-width: 250px;
    min-height: 35px;
    height: auto;
    line-height: 1rem;

    &--max-width-160 {
      max-width: 160px;
    }
      
    &--max-width-unset {
      max-width: unset;
    }

    &--max-width-190 {
      max-width: 190px;
    }

    &--small {
      max-width: 80px;
      min-width: 80px;
    }

    &--large {
      max-width: 260px;
      min-width: 260px; 
    }
      
    .cs__control {
      min-height: 35px;
      height: auto;
    }

    @media (max-width: 1350px) {
      &--date-width {
        max-width: 130px;
      }
    }

    @media (max-width: 768px) {
      max-width: none;
    }
  }

  // Alert danger
  .alert-danger {
    color: #ef476f;
    background-color: transparent;
    border-color: #ef476f;
    transition: 0.3s ease;
  }

  .alert-dismissible {
    padding-right: 3rem;
  }

  // Password strength bar
  .c-pass-strength-bar {
    > div {
      > div {
        height: 3px !important;
        cursor: pointer;
      }
    }

    .c-score-word {
      display: none;
      position: absolute;
      top: 6px;
      right: 0;
      background: #000;
      color: #fff !important;;
      padding: 0.25rem 0.5rem;
      border-radius: 3px;
      z-index: 1;

      &:after {
        bottom: 100%;
        right: 6px;
        border: solid transparent;
        content: "";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border-color: rgba(0, 0, 0, 0);
        border-bottom-color: #000;
        border-width: 6px;
        margin-left: -6px;
      }
    }

    &:hover {
      .c-score-word {
        display: block;
      }
    }
  }

  // Checkbox
  .custom-checkbox,
  .custom-radio {
    margin: 0.375rem 0;
  }

  // Switch
  .custom-switch__desc {
    margin-top: -2.5rem;
    margin-left: 3.25rem;
  }

  // Table
  .table thead th {
    vertical-align: middle;

    @media (max-width: 768px) {
      padding: 0.3rem;
    }
  }

  .table td {
    vertical-align: middle;

    @media (max-width: 768px) {
      padding: 0.5rem;
    }
  }

  .table-responsive {
    overflow-x: hidden;
    @media (max-width: 1200px) {
      overflow-x: auto;
    }
  }

  .c-row--expanded {
    background-color: #eaf8f5;

    &:hover {
      background-color: #eaf8f5 !important;
    }

    &__content {
      background-color: #eaf8f5;

      &:hover {
        background-color: #eaf8f5 !important;
      }

      & > td {
        padding: 0;
        border-color: #cbeee7;
      }
    }
  }
  .table-with-custom-dropdowns {
    .multi-dropdown {
      @media (max-width: 1300px) {
        .cs__control {
          min-width: 110px;
          display: flex;
          flex-direction: column;

          .cs__value-container {
            & > div {
              .cs__placeholder {
                width: 100%;
              }
            }

            &:not(.cs__value-container--has-value) {
              width: 100%;

              .cs__placeholder {
                width: 100%;
                left: 0;
                text-align: center;
              }
            }
          }

          div:nth-of-type(2) {
             text-align: center;
             align-self: center;
          }

          // Hide separator '|' by default
          .cs__indicators {
            .cs__indicator-separator {
              display: none;
            }

            // If there is 'clear-indicator' class ('X' icon), show separator '|'
            div.cs__indicator.cs__clear-indicator ~ span.cs__indicator-separator {
              display: block !important;
            }
          }
        }

        .cs__menu {
          min-width: 150px;
        }
      }
    }
  }

  .hover-style {
    background-color: #f7f6fa !important;
  }

  // Expanded row table
  .c-table--expanded {
    margin-bottom: 0;
    border-bottom: 1px solid #000;

    .thead-light th {
      background-color: #cbeee7;
      border-color: #cbeee7;
      padding: 0.25rem 0.75rem;
    }

    tbody tr:hover {
      background-color: #eaf8f5 !important;
    }

    td {
      vertical-align: top;
    }
  }

  // Pagination
  .c-page-size-control {
    width: 60px;
  }

  // Date picker
  .react-datepicker {
    border-color: #dee2e6;
    color: #575962;
    display: flex;

    &-popper[data-placement^="bottom"] .react-datepicker__triangle {
      border-bottom-color: #f2f3f8;
      left: 50% !important;

      &::before {
        border-bottom-color: #dee2e6;
      }
    }
    &-popper {
      z-index: 1040;
    }

    &-popper[data-placement^="top"] .react-datepicker__triangle {
      left: 50% !important;

      &::before {
        border-top-color: #dee2e6;
      }
    }

    &__header {
      background-color: #f2f3f8;
      border-bottom: 1px solid #dee2e6;
    }

    &__current-month,
    &-time__header,
    &-year-header {
      color: #575962;
    }

    &__day, &__day-name {
      color: #575962;
    }

    &__day--selected,
    &__year-text--selected,
    &__day--keyboard-selected,
    &__year-text--keyboard-selected,
    &__time-list-item--selected {
      background-color: #32bfa3 !important;
      outline-color: rgba(81, 201, 177, 0.5);
      color: #fff;

      &:hover, &:active, &:focus {
        outline-color: rgba(81, 201, 177, 0.5);
        background-color: #2aa189;
      }
    }

    &__day--disabled,
    &__month-text--disabled,
    &__quarter-text--disabled,
    &__year-text--disabled {
      cursor: default;
      color: #ccc;
    }

    &__time-container {
      border-left: 1px solid #dee2e6;
    }

    &__close-icon::after {
      color: #ced4da;
      background: transparent;
      font-size: 24px;
    }

    &__close-icon:hover::after {
      color: #575962;
    }
  }

  // Calendar row
  .rbc-row-segment {
    overflow-hidden;
  }

  // Cursor helper
  .u-cursor-pointer {
    cursor: pointer;
  }

  //React Big Calendar custom list-style-position:
  .rbc-calendar {
    overflow: auto;

    & > div:nth-of-type(1) {
      position: sticky;
      left: 0;

      h5 {
        min-width: 10rem;
      }
    }

    .rbc-month-view {
      min-width: 62.5rem;
    }
  }

  // React Big Calendar custom popup style"
  .rbc-overlay {
    max-height: 250px;
    overflow-y: scroll;
  }

  .form-control {
    height: calc(1.5em + 0.75rem + 5px);
  }

  // ToastStyles.css
  .Toastify__toast-container--top-right {
    top: 75px !important;
  }
  
  .Toastify__toast-container--top-left {
    top: 75px !important;
  }
`;
